<template>
    <div class="container">
        <div class="header">
            <h1>Version history</h1>
        </div>
        <div class="groups">
            <div
                v-for="(group, i) in groupedHistoryEntries"
                :key="i"
                class="group"
            >
                <div class="description">
                    <small
                        ><b>{{ group.description }}</b></small
                    >
                </div>
                <div
                    v-for="commit in group.commits"
                    :key="commit.id"
                    class="commit"
                    :class="{
                        'selected':
                            commit.id === selectedCommit?.id ||
                            (route.params.version === 'current' &&
                                !isEditing &&
                                commit.id === lastCommit?.id),
                        'comparison':
                            compare && commit.id === previousCommit?.id,
                    }"
                >
                    <RouterLink
                        :to="{
                            name: route.name,
                            params: {
                                ...route.params,
                                version: commit.id,
                            },
                            query: route.query,
                        }"
                    >
                        <div class="commit-description">
                            <span>
                                {{
                                    commit.author.date.toLocaleString(
                                        undefined,
                                        {
                                            day: "numeric",
                                            month: "short",
                                            hour: "numeric",
                                            hour12: false,
                                            minute: "2-digit",
                                        }
                                    )
                                }}
                            </span>
                            <span v-if="commit.id === lastCommit?.id">
                                <small><i>Current version</i></small>
                            </span>
                        </div>

                        <p :title="commit.body">{{ commit.subject }}</p>
                        <p class="author">by {{ commit.author.name }}</p>
                    </RouterLink>
                </div>
            </div>
        </div>
        <div class="action-buttons">
            <div class="action-button switch">
                <ToggleSwitch inputId="compare" v-model="compare" />
                <label for="compare">Side-by-side</label>
            </div>
            <div class="action-button">
                <Button
                    class="p-button-secondary"
                    label="Close history"
                    @click="
                        router.push({
                            name: route.name,
                            params: {
                                ...route.params,
                                version: undefined,
                            },
                        })
                    "
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useEditManager } from "@/composables/edit-manager";
import { useRoute, useRouter } from "vue-router";
import { useHistoryManager } from "@/stores/history";

const { isEditing } = useEditManager();
const route = useRoute();
const router = useRouter();
const { selectedCommit, previousCommit, groupedHistoryEntries, lastCommit } =
    useHistoryManager();

const toggleCompare = () => {
    router.push({
        name: route.name,
        params: route.params,
        query: {
            ...route.query,
            compare: route.query.compare === undefined ? "1" : undefined,
        },
    });
};

const compare = computed({
    get() {
        return route.query.compare !== undefined;
    },
    set() {
        toggleCompare();
    },
});
</script>

<style lang="scss" scoped>
.container {
    overflow-y: auto;
    /* Minus the header height. */
    max-height: calc(100vh - 57px);
    flex: 1;
    display: flex;
    flex-direction: column;
}

.header {
    position: sticky;
    top: 0;
    padding-left: 16px;
    padding-right: 16px;
    /* So text won't appear right behind the header.
       Docs on Canvas: https://web.dev/articles/color-scheme */
    background-color: Canvas;

    h1 {
        /* Same as margin-top which is hardcoded in typography.scss, we should
        think about handling spacing in a nicer way. Using padding to keep
        reserving the space even though the user scrolls. */
        padding-top: 0.375em;
        padding-bottom: 0.375em;
        margin-bottom: 0;
        display: flex;
        justify-content: center;
    }
}

.commit-description {
    display: flex;
    justify-content: space-between;
    align-items: first baseline;

    > span:first-child {
        font-size: var(--text-xl);
        font-weight: 200;
    }

    > span:nth-child(2) {
        flex-grow: 1;
        text-align: center;
    }
}

.groups {
    flex: 1;
}

.group {
    margin-bottom: 8px;

    .description {
        margin-left: 8px;
        margin-bottom: 4px;
    }

    p {
        margin-bottom: 4px;
    }

    .commit {
        a {
            text-decoration: none;
        }

        padding-top: 4px;
        padding-left: 16px;
        padding-bottom: 4px;
        transition: 150ms ease-in-out;

        &:hover {
            /**
             * Cannot use p-datatable-hover-background,
             * only when p-datatable is used prior to loading this page
             */
            background: var(--stekz-dark-gray);
        }
        &.comparison {
            background: var(--stekz-primary-background);

            &:hover {
                background: var(--stekz-dark-gray);
            }
        }
        &.selected {
            background: var(--stekz-primary-color);
            color: var(--stekz-white);

            &:hover {
                background: var(--stekz-black);
            }
        }
    }
}

.action-buttons {
    position: sticky;
    background-color: Canvas;
    bottom: 0;
    display: flex;
    padding: 16px;
    flex-direction: column;
    align-items: center;
    gap: 16px;

    .action-button.switch {
        display: flex;
        line-height: 1.5rem;

        > label {
            margin-left: 8px;
        }
    }
}
</style>
